import Modal from 'components/common/Modal/Modal';
import FormInput from 'components/FormInput';
import SearchSelect from 'components/SearchSelect';
import { getInviteStatus, roles, userRoles } from 'constants/constants';
import { SendInviteSchema } from 'constants/ValidationSchema';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { Badge, Button, Card, Form, Tab, Tabs } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Team } from 'redux/projectSlice';
import {
  useDeleteTeamMutation,
  useGetTeamByProjectIdMutation,
  useSendInviteMutation,
} from 'redux/services/teams';
import { RootStates } from 'redux/store';
import { Close, HelpOutline, QuestionAnswer } from '@mui/icons-material';
import './Teams.scss';
import { IconButton } from '@mui/material';
import { RolesInfoDialog } from './RolesInfoDialog';

export type TeamProps = {
  isSingleEdit?: boolean;
  disabled?: boolean;
};

const Teams: React.FC<TeamProps> = ({ isSingleEdit, disabled }) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const { setupId: projectId } = useParams();
  const [teamsList, setTeamsList] = useState([]);
  const [deleteMember, setDeleteMember] = useState<Team | null>(null);
  const [showRolesInfo, setShowRolesInfo] = useState(false);
  const [sendInvite, { isLoading: isSendInviteLoading }] =
    useSendInviteMutation();
  const [getTeamByProjectId] = useGetTeamByProjectIdMutation();
  const [deleteTeam, { isLoading: isDeleteLoading }] = useDeleteTeamMutation();
  const user = useSelector((state: RootStates) => state.auth.userDetails);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const formik = useFormik({
    initialValues: {
      email: '',
      mobile: '',
      role: '',
      doerType: '',
    },
    validationSchema: SendInviteSchema,
    onSubmit: async (val) => {
      const body = {
        values: {
          projectId,
          members: [val],
        },
      };
      const res: any = await sendInvite(body);
      if (res.data) {
        toast.success(`Invitation sent to ${val.email}`);
        formik.resetForm();
        getTeamsList();
        handleClose();
      } else toast.error(res.error.data.message);
    },
  });

  const getTeamsList = async () => {
    const body = {
      projectId,
    };
    const res: any = await getTeamByProjectId(body);
    if (res.data) {
      setTeamsList(res.data);
    }
  };

  const deleteTeamMember = async () => {
    const body = {
      projectId,
      values: {
        email: deleteMember?.email,
        mobile: deleteMember?.mobile,
      },
    };
    const res: any = await deleteTeam(body);
    if (res.data) {
      handleClose();
      getTeamsList();
      toast.success('Deleted');
    } else toast.error(res.error.data.message);
  };

  useEffect(() => {
    getTeamsList();
  }, []);

  const handleShowRolesInfo = () => setShowRolesInfo(true);
  const handleCloseRolesInfo = () => setShowRolesInfo(false);

  return (
    <>
      <div className="teams h-100 overflow-auto d-flex flex-column">
        <div className="step-wrapper flex-grow-1 overflow-auto">
          <div className="text-end mb-3">
            <Button
              variant="primary"
              hidden={disabled}
              onClick={() => {
                setDeleteMember(null);
                handleShow();
              }}
            >
              {t('btn_add_user')}
            </Button>
          </div>
          <div className="d-flex flex-grow-1 overflow-auto gap-3 flex-column flex-md-row flex-md-wrap">
            {teamsList.map((list: Team, index: number) => (
              <Card key={index} className="team-member">
                <Card.Body className="px-0 py-3 w-100 d-flex flex-column">
                  {list.role.toLowerCase() !== 'owner' && (
                    <Button
                      variant="link"
                      className="py-0 ms-auto position-absolute close-btn"
                      hidden={disabled}
                      onClick={() => {
                        setDeleteMember(list);
                        handleShow();
                      }}
                    >
                      <Close />
                    </Button>
                  )}
                  <div className="d-flex align-items-start team-member-info w-100 overflow-auto">
                    <div className="d-flex px-3 align-items-center pb-2 flex-grow-1 overflow-auto">
                      <img
                        src={`https://ui-avatars.com/api/?size=50&color=fff&rounded=true&bold=true&background=random&name=${
                          list.name || list.email || 'User'
                        }`}
                        alt=""
                      />
                      <div className="user-contact-container d-flex flex-column ms-3 flex-grow-1 overflow-auto">
                        <h3 className="mb-0">
                          {list.name ||
                            (list.email?.split('@')?.[0] ?? '') ||
                            list.mobile ||
                            'User'}
                        </h3>
                        <p className="mb-0" title={list?.email}>
                          {list?.email ?? ''}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex px-3 pt-3 align-items-center flex-grow-1 justify-content-between">
                    {list.userId === user?._id ? (
                      <span className="mb-0 text-primary">{`# You are ${
                        list.role
                      }${list.doerType ? `(${list.doerType})` : ''}`}</span>
                    ) : (
                      <span>{`${
                        list.doerType
                          ? `${list.role}(${list.doerType})`
                          : list.role
                      }`}</span>
                    )}
                    <Badge
                      bg={
                        list.status.toLowerCase() === 'joined'
                          ? 'success'
                          : 'danger'
                      }
                      className={`ms-3 px-2`}
                    >
                      {getInviteStatus[list.status || 'JOINED']}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        </div>
        {isSingleEdit ? (
          <div className="d-flex justify-space-between mt-2">
            <Link
              to={`/project/${projectId}`}
              className="btn btn-outline-primary"
            >
              {t('btn_back')}
            </Link>
            {!disabled && (
              <Link
                to={`/project/${projectId}`}
                className="btn btn-primary ms-auto"
              >
                {t('btn_save-exit')}
              </Link>
            )}
          </div>
        ) : (
          <div className="d-flex justify-space-between mt-2">
            <Link
              to={`/project/setup/${projectId}/1`}
              className="btn btn-outline-primary"
            >
              {t('btn_back')}
            </Link>
            <Link
              to={`/project/setup/${projectId}/3`}
              className="btn btn-primary ms-auto"
            >
              {t('btn_save-next')}
            </Link>
          </div>
        )}
      </div>
      {showRolesInfo && <RolesInfoDialog onClose={handleCloseRolesInfo} />}
      <Modal
        show={show}
        title={
          deleteMember
            ? t('modal_title_delete_team_member')
            : t('modal_title_invite_your_team')
        }
        cancelBtnText={t('btn_cancel')}
        withIcon={deleteMember ? true : false}
        onClose={handleClose}
        onSubmit={deleteMember ? deleteTeamMember : formik.handleSubmit}
        submitBtnText={deleteMember ? t('btn_delete') : t('btn_invite')}
        modalClassName="invite-team-modal"
        modalBodyClassName="pt-0 px-0"
        footerContentAlignment={deleteMember ? undefined : 'center'}
        disabled={deleteMember ? isDeleteLoading : isSendInviteLoading}
      >
        {!deleteMember && (
          <Tabs
            defaultActiveKey="email"
            className="mb-3 w-100 mx-0"
            onSelect={(tab) => {
              if (tab === 'sms') {
                formik.setFieldValue('email', '');
              } else formik.setFieldValue('mobile', '');
            }}
          >
            <Tab eventKey="email" title="By Email">
              <div className="px-4">
                <FormInput
                  label={t('email')}
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                />
                <Form.Label>
                  {t('label_role')} <span>*</span>
                  <IconButton onClick={handleShowRolesInfo}>
                    <HelpOutline />
                  </IconButton>
                </Form.Label>
                <SearchSelect
                  name="role"
                  options={roles}
                  value={formik.values.role}
                  onChange={(val) => formik.setFieldValue('role', val.value)}
                />
                {formik.values.role === userRoles.DOER && (
                  <FormInput
                    label={t('contratorType')}
                    name="doerType"
                    className="mt-3"
                    required={false}
                    value={formik.values.doerType}
                    onChange={formik.handleChange}
                  />
                )}
              </div>
            </Tab>
            <Tab eventKey="sms" title="By SMS">
              <div className="px-4">
                <FormInput
                  label={t('mobile')}
                  name="mobile"
                  value={formik.values.mobile}
                  onChange={formik.handleChange}
                />
                <Form.Label>
                  Role <span>*</span>
                  <IconButton onClick={handleShowRolesInfo}>
                    <HelpOutline />
                  </IconButton>
                </Form.Label>
                <SearchSelect
                  name="role"
                  value={formik.values.role}
                  options={roles}
                  onChange={(val) => formik.setFieldValue('role', val.value)}
                />
                {formik.values.role === userRoles.DOER && (
                  <FormInput
                    label={t('contratorType')}
                    name="doerType"
                    className="mt-3"
                    required={false}
                    value={formik.values.doerType}
                    onChange={formik.handleChange}
                  />
                )}
              </div>
            </Tab>
          </Tabs>
        )}
      </Modal>
    </>
  );
};

export default Teams;
